import { Card, CardContent, Badge } from "@pushpress/pushpress-ui";
import { Landmark, CreditCard } from "lucide-react";
import { cn } from "../../../../../utils/css";
import visaLogo from "./assets/icons/visa_logo.png";
import mastercardLogo from "./assets/icons/mastercard_logo.png";
import discoverLogo from "./assets/icons/discover_logo.png";
import amexLogo from "./assets/icons/amex_logo.png";
import stripeLinkLogo from "./assets/icons/stripe_link_logo.png";
import { isPaymentMethodExpired } from "../../../helpers";
import { Billing, BILLING_TYPE } from "../../../../../apis/billingApi";

const getCardIcon = (brand: string) => {
  switch (brand.toLowerCase()) {
    case "visa":
      return (
        <img
          src={visaLogo}
          alt="VISA logo"
          data-testid="visa-logo"
          className="tw-h-3 tw-w-auto"
        />
      );
    case "mastercard":
      return (
        <img
          src={mastercardLogo}
          alt="Mastercard logo"
          data-testid="mastercard-logo"
          className="tw-h-5 tw-w-auto"
        />
      );
    case "amex":
      return <img src={amexLogo} alt="AMEX logo" className="tw-h-5 tw-w-6" />;

    case "discover":
      return (
        <img
          src={discoverLogo}
          alt="Discover logo"
          data-testid="discover-logo"
          className="tw-h-3 tw-w-auto"
        />
      );
    default:
      return <CreditCard className="tw-h-4 tw-w-4" />;
  }
};

type PaymentMethodCardProps = {
  method: Billing;
  userUuid: string;
};

export default function PaymentMethodCard({
  method,
  userUuid,
}: PaymentMethodCardProps) {
  const isExpired = isPaymentMethodExpired(
    method.billingExpMonth,
    method.billingExpYear,
  );

  const isCreditCard = method.billingType === BILLING_TYPE.CREDIT_CARD;
  const isStripeLink = method.billingType === BILLING_TYPE.STRIPE_LINK;
  const isPrimary = !!method.isPrimary;
  const showExpirationDate =
    isCreditCard && !!method.billingExpMonth && !!method.billingExpYear;

  return (
    <Card
      key={method.id}
      className={cn(
        "tw-border tw-overflow-hidden tw-cursor-pointer tw-h-28 hover:tw-shadow-md",
        isExpired ? "tw-border-warning-medium tw-bg-warning-light" : "",
        method.isPrimary ? "tw-border-primary-medium" : "",
      )}
      onClick={() => {
        window.open(
          `/members/view/${userUuid}`,
          "_blank",
          "noopener,noreferrer",
        );
      }}
    >
      <CardContent className="!tw-px-4 !tw-py-2.5 tw-flex tw-flex-col tw-justify-between">
        <div className="tw-flex tw-items-center tw-gap-2">
          {isCreditCard ? (
            <>
              {method.billingBrand && getCardIcon(method.billingBrand)}
              <span className="tw-text-base tw-font-medium">
                •••• {method.billingLastFour}
              </span>
            </>
          ) : isStripeLink ? (
            <div className="tw-flex tw-items-center tw-gap-2 tw-h-6.5">
              <img
                src={stripeLinkLogo}
                alt="Stripe Link logo"
                data-testid="stripe-link-logo"
                className="tw-h-4 tw-w-auto"
              />
              <span className="tw-text-base tw-font-medium"> ••••</span>
            </div>
          ) : (
            <div className="tw-flex tw-items-center tw-gap-2">
              <Landmark className="tw-h-4 tw-w-4" />
              <span className="tw-text-base tw-font-medium">
                •••• {method.billingLastFour}
              </span>
            </div>
          )}
        </div>
        {showExpirationDate ? (
          <span className="tw-text-sm tw-text-neutral-500 tw-mt-2">
            Expires {method.billingExpMonth.padStart(2, "0")}/
            {method.billingExpYear}
          </span>
        ) : (
          <span className="tw-mt-2 tw-h-5" />
        )}
        <div className="tw-flex tw-flex-row tw-gap-2 tw-mt-2.5">
          {isPrimary && (
            <div className="tw-mt-1">
              <Badge>Default</Badge>
            </div>
          )}
          {!isPrimary && (
            <div className="tw-mt-1">
              <Badge variant="warning">Inactive</Badge>
            </div>
          )}
          {isExpired && (
            <div className="tw-mt-1">
              <Badge variant="warning">Expired</Badge>
            </div>
          )}
          {method.isVerified && (
            <div className="tw-mt-1">
              <Badge variant="success">Verified</Badge>
            </div>
          )}
          {!method.isVerified && (
            <div className="tw-mt-1">
              <Badge variant="error">Unverified</Badge>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
